import FocusTrapper from './class-focus-trapper';
import { syncHeight } from '../helpers/dom-tools';

/**
 * Sync height to help with modal scroll locking.
 */
window.addEventListener('resize', syncHeight);

/**
 * Mobile Menu class.
 *
 * Used in conjunction with `dreamy_Mobile_Nav_Walker` to render multi-level menu.
 * Allows conditional settings for:
 * - hasAlert: if site should handle top alert bar closing
 * - hasSubmenu: enable dropdowns
 * - hasButtonText: mobile menu trigger has 'close' / 'open' text
 * - trapFocus: should the menu trap focus within on open
 *
 * Notes:
 * - Selectors are hard coded into the class, if classes change then ensure selectors are matched.
 * - Submenus can handle 5 levels deep or more
 */
class MobileMenu {
	/**
	 * Default config.
	 */
	config = {
		hasAlert: false,
		hasSubmenu: false,
		hasButtonText: false,
		trapFocus: true,
	};

	/**
	 * State.
	 */
	isOpen = false;
	timeOutTimer = null;
	lastScrollPosition = 0;

	/**
	 * Elements.
	 */
	html = document.querySelector('html');
	body = this.html.querySelector('body');
	trigger = null;
	overlay = null;
	overlayInner = null;
	openText = null;
	closeText = null;
	alertBar = null;
	submenus = null;

	/**
	 * Focus Trapper.
	 *
	 * @type {FocusTrapper}
	 */
	focusTrapper = null;

	/**
	 * Set up the menu.
	 *
	 * @param {Object} config The config object.
	 */
	constructor(config = {}) {
		this.config = { ...this.config, ...config };
		this.trigger = document.querySelector('.js-mobile-menu-control');
		this.overlay = document.querySelector('.js-mobile-overlay');
		this.overlayInner = this.overlay.querySelector('.js-mobile-overlay-inner');
		this.alertBar = document.querySelector('.alert-bar');

		/**
		 * Handle optional button text.
		 */
		if (this.config.hasButtonText) {
			this.openText = document.querySelector('.hamburger-control__open-label');
			this.closeText = document.querySelector('.hamburger-control__close-label');
		}

		/**
		 * Bind events.
		 */
		this.trigger.addEventListener('click', this.delegateOpenClose.bind(this));
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.isOpen) {
				this.closeMenu(e);
			}
		});

		/**
		 * Bind submenus.
		 * Based on config settings.
		 */
		if (this.config.hasSubmenu) {
			this.submenus = this.overlay.querySelector('.primary-menu').querySelectorAll('.menu-item-has-children');
			this.bindSubmenus();
		}

		/**
		 * Bind alert.
		 * Based on config settings.
		 */
		if (this.config.hasAlert) {
			this.alertBar = document.querySelector('.alert-bar');
		}

		/**
		 * Setup trap focus.
		 */
		if (this.config.trapFocus) {
			this.focusTrapper = new FocusTrapper(
				this.overlay,
				[ 'a.site-header__item_inner', '.js-mobile-menu-control' ],
				[]
			);
		}
	}

	/**
	 * Delegate open/close event.
	 *
	 * @param {Event} e           The html event.
	 * @param {any}   eventCancel Cancel action.
	 */
	delegateOpenClose(e, eventCancel = null) {
		if (this.isOpen) {
			this.closeMenu(e, eventCancel);
		} else {
			this.openMenu(e);
		}
	}

	/**
	 * Handle opening menu.
	 *
	 * @param {Event} e Dom event.
	 */
	openMenu(e) {
		e.preventDefault();
		clearTimeout(this.timeOutTimer);

		// Collpase the alert if necessary.
		if (this.config.hasAlert) {
			this.collapseAlert();
		}

		this.trigger.classList.add('is-active');

		// Optional button text.
		if (this.config.hasButtonText) {
			this.openText.setAttribute('aria-hidden', false);
			this.openText.style.display = 'none';
			this.closeText.setAttribute('aria-hidden', true);
			this.closeText.style.display = 'block';
		}

		// Set current scroll position.
		this.lastScrollPosition = this.html.scrollTop;

		// Start transitioning.
		this.overlay.classList.add('menu--pre-open', 'temp-high-z');
		this.overlay.setAttribute('hidden', false);

		this.timeOutTimer = setTimeout(() => {
			this.overlay.classList.add('menu--open');
			this.html.classList.add('overlay-active', 'mobile-menu-active');
			this.overlayInner.scrollTop = 0;
		}, 2);

		// Lock scroll.
		this.handleScrollLock(true);

		// Trap focus.
		if (this.config.trapFocus) {
			this.focusTrapper.bind();
		}

		this.isOpen = true;
	}

	/**
	 * Handle closing menu.
	 *
	 * @param {Event}   e           The event object.
	 * @param {boolean} cancelEvent Event cancel.
	 */
	closeMenu(e, cancelEvent) {
		e.preventDefault();
		clearTimeout(this.timeOutTimer);

		if (cancelEvent && e) {
			e.preventDefault();
			e.stopPropagation();
		}

		// Realse scroll lock.
		this.handleScrollLock(false);

		// Start transitioning.
		this.trigger.classList.remove('is-active');
		this.html.classList.remove('overlay-active', 'mobile-menu-active');
		this.overlay.classList.remove('menu--open');

		// Optional button text.
		if (this.config.hasButtonText) {
			this.closeText.setAttribute('aria-hidden', false);
			this.closeText.style.display = 'none';
			this.openText.setAttribute('aria-hidden', true);
			this.openText.style.display = 'block';
		}

		// Collpase the alert if necessary.
		if (this.config.hasAlert) {
			this.expandAlert();
		}

		// Helps prevent flicker on IOS.
		setTimeout(() => {
			this.overlay.classList.remove('temp-high-z', 'menu--pre-open');
			this.overlay.setAttribute('hidden', true);
		}, 200);

		// Relase trap focus.
		if (this.config.trapFocus) {
			this.focusTrapper.unbind();
		}

		this.isOpen = false;
	}

	/**
	 * Named func for preventing scrolling event.
	 * Must be named to allow unbinding.
	 *
	 * @param {Event} e An event.
	 */
	preventDefaultNamed(e) {
		e.preventDefault();
	}

	/**
	 * Handle locking scroll.
	 * See additional CSS added by .mobile-menu-active.
	 * Uses global lastScrollPosition.
	 *
	 * @param {boolean} opening If opening or closing.
	 */
	handleScrollLock(opening) {
		if (opening === true) {
			// Lock scroll.
			this.overlay.addEventListener('pointermove', this.preventDefaultNamed);
			this.html.classList.add('scroll-lock');
			this.body.style.top = `-${ this.lastScrollPosition }px`;
		} else {
			// Relase scroll lock.
			this.overlay.removeEventListener('pointermove', this.preventDefaultNamed);
			this.html.classList.remove('scroll-lock');
			this.body.style.top = '';

			// Use 'instant' to prevent scroll jump.
			// - 1 weirdly helps on IOS.
			window.scrollTo({
				top: this.lastScrollPosition - 1,
				left: 0,
				behavior: 'instant',
			});
		}
	}

	/**
	 * Handle submenus.
	 */
	bindSubmenus() {
		this.submenus.forEach((s, i) => {
			const trigger = s.querySelector('.expand-trigger');
			const panel = s.querySelector('.mobile-sub-menu');

			// Set aria.
			this.assignAriaTriggers(trigger, panel, i);

			// Handle sublink focus.
			this.handleSublinkFocus(panel);

			// Register events.
			this.registerSubmenuEvent(trigger, panel);
		});
	}

	/**
	 * Assign aria labels to sub menus and triggers.
	 *
	 * @param {Node}   trigger The trigger element.
	 * @param {Node}   panel   The panel element.
	 * @param {number} i       The index of the trigger.
	 */
	assignAriaTriggers(trigger, panel, i) {
		trigger.setAttribute('aria-controls', `sub-menu-${ i }`);
		trigger.setAttribute('id', `menu-trigger-${ i }`);
		panel.setAttribute('id', `sub-menu-${ i }`);
		panel.setAttribute('aria-labelledby', `menu-trigger-${ i }`);
	}

	/**
	 * Prevent focus on all sub links/buttons.
	 *
	 * @param {Node}    panel    The panel element.
	 * @param {boolean} isActive The panel status.
	 */
	handleSublinkFocus(panel, isActive = false) {
		/**
		 * Iterate through panels, updating aria appropriately.
		 */
		[ panel, ...panel.querySelectorAll('.mobile-sub-menu') ].forEach((m) => {
			let focusTargets = [];
			// submenu > ul > [li]
			Array.from(m.children[0].children).forEach((li) => {
				focusTargets = [ ...focusTargets, ...li.querySelectorAll(':scope > a, :scope > button') ];
			});
			/**
			 * Close top level,
			 * Force all unfocusable.
			 */
			if (! isActive) {
				focusTargets.forEach((l) => l.setAttribute('tabindex', '-1'));
			}
			/**
			 * Open top level,
			 * If submenu is-active allow focus.
			 */
			if (isActive && m.classList.contains('is-active')) {
				focusTargets.forEach((l) => l.removeAttribute('tabindex'));
			}
		});
	}

	/**
	 * Handle click event.
	 *
	 * @param {Node} trigger The trigger element.
	 * @param {Node} panel   The panel element.
	 */
	registerSubmenuEvent(trigger, panel) {
		trigger.addEventListener('click', (e) => {
			e.preventDefault();

			// Set class.
			trigger.classList.toggle('is-active');
			panel.classList.toggle('is-active');
			const state = trigger.classList.contains('is-active');

			// Set aria attrs.
			trigger.setAttribute(
				'aria-expanded',
				state
			);

			// Animate in.
			if (panel.style.maxHeight) {
				panel.style.maxHeight = null;
			} else {
				/**
				 * Calculate max height of sub menu.
				 * Consider every nested sub menu.
				 */
				let h = panel.scrollHeight;
				panel.querySelectorAll('.mobile-sub-menu').forEach((s) => {
					h += s.scrollHeight;
				});
				panel.style.maxHeight = h + 'px';
			}

			// Handle sublink focus.
			this.handleSublinkFocus(panel, state);
		});
	}

	/**
	 * Handle Alert.
	 */

	/**
	 * Handle closing alert.
	 * Takes inner content height and explicitly transitions outer
	 * to 0px.
	 */
	collapseAlert() {
		// Will proceed if alert is visible and not collapsed.
		if (
			this.alertBar &&
			this.alertBar.style.display !== 'none' &&
			this.alertBar.getAttribute('data-collapsed') !== 'true'
		) {
			const sectionHeight = this.alertBar.getBoundingClientRect().height;
			const elementTransition = this.alertBar.style.transition;
			this.alertBar.style.transition = '';
			requestAnimationFrame(() => {
				this.alertBar.style.height = sectionHeight + 'px';
				this.alertBar.style.transition = elementTransition;
				requestAnimationFrame(() => {
					this.alertBar.style.height = 0 + 'px';
				});
			});
			this.alertBar.setAttribute('data-collapsed', 'true');
		}
	}

	/**
	 * Handle expanding alert.
	 * Takes inner content height and explicitly transitions outer
	 * to inner height. Removes height to allow auto flow.
	 */
	expandAlert() {
		if (
			this.alertBar &&
			this.alertBar.style.display !== 'none' &&
			this.alertBar.getAttribute('data-collapsed') === 'true'
		) {
			const sectionHeight = this.alertBar.children[0].getBoundingClientRect().height;
			this.alertBar.style.height = sectionHeight + 'px';
			const transFunc = () => {
				this.alertBar.removeEventListener('transitionend', transFunc);
				this.alertBar.style.height = null;
			};
			this.alertBar.addEventListener('transitionend', transFunc);
			this.alertBar.setAttribute('data-collapsed', 'false');
		}
	}
}

new MobileMenu({
	hasSubmenu: true,
	hasAlert: true,
	hasButtonText: false,
});
